import { Controller } from "@hotwired/stimulus"
import { createElement } from "../src/table_filters/dom"
import { createFilter } from "../src/table_filters/registry"

const clone = (value) => JSON.parse(JSON.stringify(value))

export default class extends Controller {
  static values = {
    filters: Object,
    locale: String,
    translations: Object,
  }

  connect() {
    if (document.documentElement.hasAttribute("data-turbo-preview")) { return }

    this.currentFilters = this.filtersFromLocation()
    this.lastAppliedFilters = clone(this.currentFilters)
    this.currentFilter = null
    this.currentFilterName = null
    this.modalOpen = false

    this.clickHandler = (event) => { this.handleClick(event) }
    this.keyupHandler = (event) => { this.handleKeyup(event) }
    document.addEventListener("click", this.clickHandler)
    document.addEventListener("keyup", this.keyupHandler)

    this.renderFilterEntries()
  }

  disconnect() {
    if (this.clickHandler) { document.removeEventListener("click", this.clickHandler) }
    if (this.keyupHandler) { document.removeEventListener("keyup", this.keyupHandler) }
    this.abortController?.abort()
    document.querySelector("[data-filter-table]")?.classList.remove("is--loading")
    this.closeModal()
  }

  handleClick(event) {
    const applyTrigger = event.target.closest("[data-filter-selection-add]")
    if (applyTrigger) {
      event.preventDefault()
      this.applyCurrentFilter()
      return
    }

    const closeTrigger = event.target.closest("[data-filter-selection-close]")
    if (closeTrigger) {
      event.preventDefault()
      this.closeModal()
      return
    }

    const option = event.target.closest("[data-filter-option]")
    if (option) {
      event.preventDefault()
      this.selectFilter(option.dataset.filterOptionName)
      return
    }

    const showTrigger = event.target.closest("[data-filter-show-modal]")
    if (showTrigger) {
      event.preventDefault()
      this.openModal()
      return
    }

    const entry = event.target.closest("[data-filter-entry]")
    if (entry) {
      event.preventDefault()
      this.removeFilter(entry.dataset.filterEntry)
    }
  }

  handleKeyup(event) {
    if (!this.modalOpen) { return }

    if (["Escape", "Esc"].includes(event.key)) { this.closeModal() }
    if (event.key === "Enter") { this.applyCurrentFilter() }
  }

  filtersFromLocation() {
    const query = new URL(window.location.href).searchParams.get("filters")
    if (!query) { return {} }

    try {
      const filters = JSON.parse(query)
      if (!filters || Array.isArray(filters) || typeof filters !== "object") { return {} }

      return Object.fromEntries(Object.entries(filters).filter(([name]) => this.filterFor(name)))
    } catch (error) {
      console.warn("Ignoring malformed table filters", error)
      return {}
    }
  }

  filterFor(name) {
    const definition = this.filtersValue[name]
    return definition ? createFilter(definition, this.filterContext) : null
  }

  get filterContext() {
    return {
      locale: this.localeValue,
      translations: this.translationsValue,
    }
  }

  openModal() {
    if (this.modalOpen) { return }

    this.modal = createElement("div", {
      className: "filter-selection__modal",
      dataset: { filterSelectionModal: "" },
    })
    this.modal.setAttribute("role", "dialog")
    this.modal.setAttribute("aria-modal", "true")

    const wrapper = createElement("div", { className: "filter-selection__wrapper" })
    const header = createElement("div", { className: "filter-selection__header" })
    const title = createElement("div", {
      className: "filter-selection__title",
      text: this.translationsValue.modalTitle,
    })
    const close = createElement("div", {
      className: "filter-selection__close-button",
      dataset: { filterSelectionClose: "" },
    })
    close.setAttribute("aria-label", this.translationsValue.close)
    close.setAttribute("role", "button")
    close.tabIndex = 0
    const icon = createElement("i", { className: "fa fa-times fa-fw" })
    icon.setAttribute("aria-hidden", "true")
    close.appendChild(icon)
    header.append(title, close)

    this.modalContent = createElement("div", {
      className: "filter-selection__content",
      dataset: { filterSelectionContent: "" },
    })
    Object.entries(this.filtersValue).forEach(([name, definition]) => {
      const option = createElement("div", {
        className: "filter-selection__option",
        dataset: { filterOption: "", filterOptionName: name },
      })
      option.appendChild(createElement("span", {
        className: "filter-selection__option-title",
        text: definition.title,
      }))

      if (definition.description) {
        option.appendChild(createElement("span", {
          className: "filter-selection__option-description",
          text: definition.description,
        }))
      }

      this.modalContent.appendChild(option)
    })

    wrapper.append(header, this.modalContent)
    this.modal.appendChild(wrapper)
    document.body.appendChild(this.modal)
    this.modalOpen = true
  }

  closeModal() {
    if (!this.modalOpen) { return }

    this.currentFilter?.disconnect()
    this.currentFilter = null
    this.currentFilterName = null
    this.modal?.remove()
    this.modal = null
    this.modalContent = null
    this.modalOpen = false
  }

  selectFilter(name) {
    const filter = this.filterFor(name)
    if (!filter || !this.modalContent) { return }

    this.currentFilter?.disconnect()
    this.currentFilter = filter
    this.currentFilterName = name

    const form = filter.createForm()
    this.modalContent.replaceChildren(form)
    filter.connect()
  }

  applyCurrentFilter() {
    if (!this.currentFilter || !this.currentFilterName) { return }

    const result = this.currentFilter.read()
    if (!result.valid) { return }

    this.currentFilters[this.currentFilterName] = this.currentFilter.serialize(result.value)
    this.renderFilterEntries()
    this.closeModal()
    this.submitFilters()
  }

  removeFilter(name) {
    if (!(name in this.currentFilters)) { return }

    delete this.currentFilters[name]
    this.renderFilterEntries()
    this.submitFilters()
  }

  renderFilterEntries() {
    const list = document.querySelector(".section-filter__list")
    if (!list) { return }

    list.querySelectorAll("[data-filter-entry]").forEach((entry) => { entry.remove() })

    Object.entries(this.currentFilters).forEach(([name, value]) => {
      const filter = this.filterFor(name)
      if (!filter) { return }

      const entry = createElement("div", {
        className: "section-filter__entry",
        dataset: { filterEntry: name },
      })
      const content = createElement("span", { dataset: { filterEntryContent: "" } })
      content.appendChild(filter.summary(value))
      const close = createElement("a", {
        className: "section-filter__entry-close-button",
        dataset: { filterEntryCloseButton: "" },
        text: "X",
      })
      close.href = "#"
      entry.append(content, close)
      list.appendChild(entry)
    })
  }

  async submitFilters() {
    this.abortController?.abort()
    const abortController = new AbortController()
    this.abortController = abortController

    const url = new URL(window.location.href)
    url.searchParams.set("filters", JSON.stringify(this.currentFilters))
    const requestedFilters = clone(this.currentFilters)
    const table = document.querySelector("[data-filter-table]")
    table?.classList.add("is--loading")

    try {
      const response = await fetch(url.toString(), {
        credentials: "same-origin",
        headers: {
          Accept: "text/html",
          "X-Requested-With": "XMLHttpRequest",
        },
        signal: abortController.signal,
      })
      if (!response.ok) { throw new Error(`Table filter request failed with status ${response.status}`) }

      const responseBody = await response.text()
      if (this.abortController !== abortController) { return }

      this.applyFilteredResults(responseBody)
      window.history.replaceState({}, document.title, url.toString())
      this.lastAppliedFilters = requestedFilters
    } catch (error) {
      if (error?.name === "AbortError") { return }

      this.currentFilters = clone(this.lastAppliedFilters)
      this.renderFilterEntries()
      document.querySelector("[data-filter-table]")?.classList.remove("is--loading")
      this.element.dispatchEvent(new CustomEvent("table-filter:error", {
        bubbles: true,
        detail: { error, url: url.toString() },
      }))
      console.error("Unable to apply table filters", error)
    } finally {
      if (this.abortController === abortController) { this.abortController = null }
    }
  }

  applyFilteredResults(responseBody) {
    const newDocument = new DOMParser().parseFromString(responseBody, "text/html")
    const currentElements = Array.from(document.querySelectorAll("[data-filter-replace]"))
    const replacements = currentElements.map((currentElement) => {
      const identifier = currentElement.dataset.filterReplace
      const newElement = Array.from(newDocument.querySelectorAll("[data-filter-replace]"))
        .find((element) => element.dataset.filterReplace === identifier)

      if (!newElement) { throw new Error(`Missing table filter replacement: ${identifier}`) }
      return { currentElement, newElement }
    })

    replacements.forEach(({ currentElement, newElement }) => { currentElement.replaceWith(newElement) })
  }
}
